// Standardized UI state rendering (Task 13.7).
//
// Wraps a section's async state and renders, in order of precedence:
//  - loading skeletons (shown immediately, within the 200 ms budget, Req 29.1),
//  - an error state with a retry control (Req 29.5),
//  - an empty state with the section's exact empty string (Req 29.2-29.4), or
//  - the loaded content.
//
// Used per dashboard section so each renders independently with error
// isolation (Req 20.9, 21.7).

import type { ReactNode } from "react";
import type { AsyncState } from "../hooks/useAsyncData";

export const ERROR_MESSAGE = "Couldn't load this. Please try again." as const;

interface UIStateWrapperProps<T> {
  state: AsyncState<T>;
  /** Exact empty-state string for this section (Req 29.2-29.4). */
  emptyText: string;
  /** Decide whether successfully-loaded data is empty. */
  isEmpty?: (data: T) => boolean;
  /** Retry handler backing the error-state control (Req 29.5). */
  onRetry?: () => void;
  /** Number of skeleton rows to show while loading. */
  skeletonRows?: number;
  children: (data: T) => ReactNode;
}

/** Loading skeleton placeholder shown within 200 ms of a request (Req 29.1). */
export function Skeleton({ rows = 3 }: { rows?: number }): JSX.Element {
  return (
    <div data-testid="skeleton" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="sb-skeleton"
          style={{ width: `${90 - i * 10}%` }}
        />
      ))}
    </div>
  );
}

export function UIStateWrapper<T>({
  state,
  emptyText,
  isEmpty,
  onRetry,
  skeletonRows = 3,
  children,
}: UIStateWrapperProps<T>): JSX.Element {
  if (state.status === "loading") {
    return <Skeleton rows={skeletonRows} />;
  }

  if (state.status === "error") {
    return (
      <div className="sb-error" role="alert">
        <p>{ERROR_MESSAGE}</p>
        {onRetry && (
          <button type="button" className="sb-btn sb-btn--secondary" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  if (isEmpty && isEmpty(state.data)) {
    return (
      <div className="sb-empty" data-testid="empty-state">
        {emptyText}
      </div>
    );
  }

  return <>{children(state.data)}</>;
}
